import React, { ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import {
  Auth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateEmail,
  updatePassword,
  User,
  ActionCodeSettings,
} from 'firebase/auth';
import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { StorageReference } from 'firebase/storage';

import { useAuthState } from 'react-firebase-hooks/auth';

import { auth, cloudFunctions, firestore, filesRef, imagesRef } from '../firebase';

export interface AuthProviderProps {
  children?: ReactNode;
}

export interface AuthUser {
  user: User | null;
  profileId: string | null | undefined;
}

export interface AuthContextModel {
  auth: Auth;
  authUrl: string;
  checkUserExists: (email: string) => Promise<boolean>;
  disableGod: (disable: boolean) => void;
  filesRef: StorageReference;
  imagesRef: StorageReference;
  isEmailVerified: boolean;
  isGod: boolean;
  isGodCapable: boolean;
  isLoggedIn: boolean;
  isLoggingIn: boolean;
  resetPassword: (email: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  authUser: AuthUser | null | undefined;
  updateUserEmail: (newEmail: string) => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  reauthenticateUser: (password: string) => Promise<void>;
  sendVerifyEmailChange: (continueUrl: string) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextModel>({} as AuthContextModel);

export function useAuth(): AuthContextModel {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }: AuthProviderProps): ReactElement => {
  // TODO: Handle user auth error
  const [user, isLoggingIn, isLoginError] = useAuthState(auth);

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isGod, setIsGod] = useState<boolean>(false);
  const [isGodCapable, setIsGodCapable] = useState<boolean>(false);
  const [authUser, setAuthUser] = useState<AuthUser | null | undefined>(null);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const authUrl = 'https://gratisports.com'; // Used for the auth action handlers.

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        console.log("User loaded.")
        setIsLoggedIn(true);
        user.getIdTokenResult().then((tokenResult) => {
          if (tokenResult?.claims?.level === 'god') {
            setIsGod(true);
            setIsGodCapable(true);
            console.log('God is here');
          }
          // Set userProfileId from claim or fall back to auth UID
          let profileId: string;
          if (tokenResult?.claims?.profileId) {
            profileId = tokenResult.claims.profileId as string;
          } else {
            profileId = user.uid;
          }
          console.log('profileId: ', profileId);
          console.log('user.uid: ', user.uid);
          // Only update authUser if profileId has changed
          if (profileId !== authUserId) {
            setAuthUserId(profileId);
            setAuthUser({user: user, profileId: profileId});
          }
        }).catch((error) => {
          console.error("Error getting token result:", error);
          // Fallback to using auth UID if token retrieval fails
          const fallbackId = user.uid;
          if (fallbackId !== authUserId) {
            setAuthUserId(fallbackId);
            setAuthUser({user: user, profileId: fallbackId});
          }
        });
      } else if (isLoginError) {
        setIsLoggedIn(false);
        setAuthUser(null);
        setAuthUserId(null);
        console.log(isLoginError);
      } else {
        setIsLoggedIn(false);
        setAuthUser(null);
        setAuthUserId(null);
      }
    });
    return unsubscribe;
  }, [user, isLoginError, authUserId]);

  async function clearFirestoreCache() {
    try {
      // First disable network
      await disableNetwork(firestore);
      // Add a small delay to ensure all listeners are cleaned up
      await new Promise((resolve) => setTimeout(resolve, 50));
      // Then re-enable
      await enableNetwork(firestore);
      console.log('Firestore cache cleared successfully.');
    } catch (error) {
      console.error('Error clearing Firestore cache:', error);
    }
  }

  function disableGod(disable: boolean) {
    if (user && isGodCapable) {
      setIsGod(disable);
      console.log(disable ? 'God is dead' : 'God is here');
    }
  }

  interface ReqCheckEmailInterface {
    email: string;
  }

  interface ResCheckEmailInterface {
    emailExists: boolean;
  }

  const checkUserExists = async (email: string): Promise<boolean> => {
    const checkAuthEmailExists = httpsCallable<ReqCheckEmailInterface, ResCheckEmailInterface>(
      cloudFunctions,
      'checkUserExists'
    );

    return checkAuthEmailExists({ email: email })
      .then((result) => {
        return result.data.emailExists;
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
  };

  const signIn = async (email: string, password: string) => {
    signInWithEmailAndPassword(auth, email, password)
      .then((/* userCredential */) => {
        // ********* setUser(userCredential.user);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  async function signOutUser(): Promise<void> {
    try {
      // First sign out of Firebase Auth
      await signOut(auth);

      // Clear react-firebase-hooks cache
      await clearFirestoreCache();

      // Clear local storage
      localStorage.clear();
      sessionStorage.clear();

      // Add a small delay to ensure all cleanup is complete
      await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Error during sign out:', error);
      throw error;
    }
  }

  function resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(auth, email);
  }

  function sendVerifyEmailChange(continueUrl: string): Promise<void> {
    if (!user) {
      throw new Error('No user logged in');
    }
    const actionCodeSettings: ActionCodeSettings = {
      url: continueUrl,
    };
    return sendEmailVerification(user, actionCodeSettings);
  }

  const reauthenticateUser = useCallback(
    async (password: string) => {
      if (!user?.email) {
        throw new Error('No user email found');
      }
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    },
    [user]
  );

  const updateUserEmail = useCallback(
    async (newEmail: string) => {
      if (!user) {
        throw new Error('No user logged in');
      }
      updateEmail(user, newEmail)
        .then(() => {
          console.log('Email updated');
        })
        .catch((error) => {
          console.error('Error updating email:', error);
        });
    },
    [user]
  );

  const updateUserPassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!user) {
        throw new Error('No user logged in');
      }
      await reauthenticateUser(currentPassword);
      await updatePassword(user, newPassword);
    },
    [user, reauthenticateUser]
  );

  const values = {
    auth,
    authUser,
    authUrl,
    disableGod,
    filesRef,
    imagesRef,
    isEmailVerified: user ? user.emailVerified : false,
    isGodCapable,
    isLoggingIn,
    isLoggedIn,
    isGod,
    checkUserExists,
    signIn,
    signOutUser,
    resetPassword,
    updateUserEmail,
    updateUserPassword,
    reauthenticateUser,
    sendVerifyEmailChange,
  };
  return (
    <AuthContext.Provider value={values}>
      {isLoggingIn && <div>Loading...</div>}
      {children}
    </AuthContext.Provider>
  );
};

//export const useUserContext = (): UserContextState => {
//  return useContext(UserStateContext)
//}
